import React from "react";
import { makeStyles } from "tss-react/mui";

import ArrowFileUp from "@dashboard/files/components/ArrowFileUp";
import { Typography, useTheme } from "@mui/material";

import { useFileDropHandlers } from "../../utils/useFileDropHandlers";

const useStyles = makeStyles()((theme) => ({
    label: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: `2px dashed ${theme.palette.emteria.border.primary}`,
        backgroundColor: theme.palette.emteria.background.paper,
        borderRadius: 12,
        padding: theme.spacing(5),
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        flex: 1,
        cursor: "pointer",
    },
    labelDragging: {
        borderColor: theme.palette.emteria.action,
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
    labelWarning: {
        borderColor: theme.palette.emteria.warning,
    },
    labelError: {
        borderColor: theme.palette.emteria.error,
    },
    labelDisabled: {
        opacity: 0.5,
        cursor: "default",
    },
    container: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    title: {
        fontSize: 18,
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
    caption: {
        fontSize: 14,
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
    selectedFileName: {
        fontSize: 16,
        wordBreak: "break-all",
        textAlign: "center",
        color: theme.palette.emteria.text.primary,
    },
}));

type Props = {
    inputId: string;
    label: React.ReactNode;
    onFilesSelected: (files: File[]) => void;
    accept?: string;
    caption?: string;
    className?: string;
    disabled?: boolean;
    error?: boolean;
    warning?: boolean;
    multiple?: boolean;
    selectedFileName?: string;
};

export const FileDropzone = (props: Props) => {
    const { classes, cx } = useStyles();
    const theme = useTheme();

    const handleFiles = (files: File[]) => {
        if (props.disabled || !files.length) {
            return;
        }

        props.onFilesSelected(props.multiple ? files : files.slice(0, 1));
    };

    const fileDropHandlers = useFileDropHandlers<HTMLLabelElement>({
        onFiles: handleFiles,
        disabled: props.disabled,
    });

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files ?? []);
        handleFiles(files);
        event.target.value = "";
    };

    return (
        <label
            htmlFor={props.inputId}
            className={cx(
                classes.label,
                fileDropHandlers.isDragging ? classes.labelDragging : null,
                props.error ? classes.labelError : props.warning ? classes.labelWarning : null,
                props.disabled ? classes.labelDisabled : null,
                props.className,
            )}
            {...fileDropHandlers.dragHandlers}
        >
            <div className={classes.container}>
                <ArrowFileUp
                    color={props.error ? theme.palette.emteria.error : props.warning ? theme.palette.emteria.warning : undefined}
                />
                {props.selectedFileName ? (
                    <Typography className={classes.selectedFileName}>{props.selectedFileName}</Typography>
                ) : (
                    <Typography className={classes.title}>{props.label}</Typography>
                )}
                {props.caption ? <Typography className={classes.caption}>{props.caption}</Typography> : null}
                <input
                    id={props.inputId}
                    type="file"
                    multiple={props.multiple}
                    accept={props.accept}
                    onChange={onChange}
                    hidden
                    disabled={props.disabled}
                />
            </div>
        </label>
    );
};
